import { MissingCredentialRequirementsPill } from "../Bookability/Credentials/MissingCredentialRequirementsPill";
import { ShiftDocumentsPill } from "../Bookability/Documents/Pill";
import { IcAgreementPill } from "../Bookability/IcAgreement/Pill";
import { LicensePill } from "../Bookability/License/Pill";
import { OrientationRequiredPill } from "../Bookability/Orientation/Pill";
import { SkillAssessmentPill } from "../Bookability/SkillAssessment/Pill";
import { StripePill } from "../Bookability/Stripe/Pill";
import { PriorityShiftPill } from "../Priority/Pill";
import { PriorityPlusShiftPill } from "../Priority/PlusPill";
import { WindowedOutPill } from "../Priority/WindowedOutPill";
import { ShiftAuctionPill } from "../ShiftAuction/Pill";
import { type OpenShiftCardPillsProps } from "./getOpenShiftCardPillsProps";
import { LimitedShiftPill } from "./LimitedShiftPill";

/**
 * Renders pills related to the open shift
 * - Priority
 * - Priority Plus (A-Team)
 * - Missing documents
 * - Windowed out
 * - Missing license
 *
 * Wrap them with ShiftCardPillsWrapper to have a consistent look
 */
export function OpenShiftCardPills(props: OpenShiftCardPillsProps) {
  const {
    unbookableWindowName,
    unbookableWindowThreshold,
    workerScore,
    licensePillStatus,
    blockingRequirementsPillContext,
    showPaymentsDisabledPill,
    showAgreementToSignPill,
    showBasketSizeLimitPill,
    showPriorityPill,
    showPriorityPlusPill,
    showLicensePill,
    showBlockingRequirementsPill,
    showDemoShiftPill,
    showUnbookableWindowPill,
    showShiftAuctionPill,
    pillsCount,
    showOrientationRequiredPill,
    showWorkplaceCredentialRequirementsPill,
    showBaseCredentialRequirementsPill,
  } = props;

  const {
    blockingRequirementsPillCount,
    areBlockingRequirementsExpiringSoon,
    blockingRequirementExpiryDate,
  } = blockingRequirementsPillContext ?? {};

  const hasMultiplePills = pillsCount > 1;

  if (!pillsCount) {
    return null;
  }

  if (showUnbookableWindowPill && unbookableWindowName) {
    return (
      <WindowedOutPill
        window={unbookableWindowName}
        workerScore={workerScore}
        windowThreshold={unbookableWindowThreshold}
      />
    );
  }

  return (
    <>
      {showBasketSizeLimitPill && <LimitedShiftPill compact={hasMultiplePills} />}

      {showShiftAuctionPill && <ShiftAuctionPill variant="icon-highlight" />}

      {showPriorityPill && <PriorityShiftPill />}

      {showPriorityPlusPill && <PriorityPlusShiftPill />}

      {showLicensePill && licensePillStatus && (
        <LicensePill status={licensePillStatus} compact={hasMultiplePills} />
      )}

      {showWorkplaceCredentialRequirementsPill && (
        <MissingCredentialRequirementsPill type="WORKPLACE_CREDENTIAL" />
      )}
      {showBaseCredentialRequirementsPill && (
        <MissingCredentialRequirementsPill type="BASE_CREDENTIAL" />
      )}

      {showPaymentsDisabledPill && <StripePill />}

      {showAgreementToSignPill && <IcAgreementPill />}

      {showDemoShiftPill && <SkillAssessmentPill />}

      {showBlockingRequirementsPill && (
        <ShiftDocumentsPill
          compact={hasMultiplePills}
          count={blockingRequirementsPillCount ?? 0}
          isExpiringSoon={areBlockingRequirementsExpiringSoon ?? false}
          expiryDate={blockingRequirementExpiryDate}
        />
      )}

      {showOrientationRequiredPill && <OrientationRequiredPill compact={hasMultiplePills} />}
    </>
  );
}
